import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Briefcase, ShoppingCart, Calendar, LogOut, MapPin, DollarSign,
  Clock, CheckCircle, FileText, HardHat, Phone, Mail, Menu, X,
  ChevronRight, AlertCircle, Download
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400', icon: HardHat },
  completed: { label: 'Completed', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  scheduled: { label: 'Scheduled', color: 'bg-blue-500/20 text-blue-400', icon: Calendar },
  approved: { label: 'Approved', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  draft: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500/20 text-blue-400', icon: Mail },
  acknowledged: { label: 'Acknowledged', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-AU', { 
    weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit' 
  });
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
};

const TradePortalPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [contractor, setContractor] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [pos, setPOs] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [activeTab, setActiveTab] = useState('jobs');
  const [selectedJob, setSelectedJob] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('trade_token');
    if (token) {
      fetchTradeData(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchTradeData = async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [meRes, jobsRes, posRes, scheduleRes] = await Promise.all([
        axios.get(`${API}/api/trade-portal/me`, { headers }),
        axios.get(`${API}/api/trade-portal/jobs`, { headers }),
        axios.get(`${API}/api/trade-portal/purchase-orders`, { headers }),
        axios.get(`${API}/api/trade-portal/schedule`, { headers }),
      ]);
      setUser(meRes.data.user);
      setContractor(meRes.data.contractor);
      setJobs(jobsRes.data);
      setPOs(posRes.data);
      setSchedule(scheduleRes.data);
      setIsLoggedIn(true);
    } catch (error) {
      localStorage.removeItem('trade_token');
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/trade-portal/login`, { email, password });
      localStorage.setItem('trade_token', response.data.token);
      toast.success('Welcome back!');
      fetchTradeData(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api/trade-portal/register`, { email, password });
      localStorage.setItem('trade_token', response.data.token);
      toast.success('Account created!');
      fetchTradeData(response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('trade_token');
    setIsLoggedIn(false);
    setUser(null);
    setContractor(null);
    toast.success('Logged out');
  };

  const handleDownloadPO = async (poId) => {
    try {
      const response = await axios.get(`${API}/api/purchase-orders/${poId}/pdf`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${localStorage.getItem('trade_token')}` }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO-${poId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Calculate stats
  const activeJobs = jobs.filter(j => j.status !== 'completed').length;
  const pendingPOs = pos.filter(p => p.status === 'sent' || p.status === 'draft').length;
  const upcomingSchedule = schedule.filter(s => new Date(s.start_datetime) > new Date()).length;
  const totalPOValue = pos.reduce((sum, p) => sum + (p.total || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Login Screen - Mobile Optimized
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="trade-login">
        <Card className="w-full max-w-sm bg-card border-border">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-3 bg-primary/20 rounded-full flex items-center justify-center">
              <HardHat className="h-8 w-8 text-primary" />
            </div>
            <CardTitle className="text-xl">{isRegistering ? 'Create Account' : 'Trade Portal'}</CardTitle>
            <p className="text-sm text-muted-foreground">Access your jobs & schedule</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
              <div>
                <Input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  className="bg-background h-12 text-base" 
                  placeholder="Email address" 
                />
              </div>
              <div>
                <Input 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                  className="bg-background h-12 text-base" 
                  placeholder="Password"
                />
              </div>
              <Button type="submit" className="w-full h-12 text-base font-semibold">
                {isRegistering ? 'Create Account' : 'Sign In'}
              </Button>
            </form>
            <Button 
              variant="link" 
              className="w-full mt-4" 
              onClick={() => setIsRegistering(!isRegistering)}
            >
              {isRegistering ? 'Already have an account?' : 'First time? Register'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main App - Mobile First Design
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="trade-portal-dashboard">
      {/* Mobile Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border safe-area-top">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
              <HardHat className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="font-bold text-sm">{contractor?.name || user?.name}</h1>
              <p className="text-xs text-muted-foreground">
                {contractor?.trade_types?.join(', ') || contractor?.trade_type || 'Contractor'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-background">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 className="font-bold">Menu</h2>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="p-4 space-y-2">
            <div className="p-4 bg-card rounded-lg border border-border mb-4">
              <p className="font-medium">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            <Button 
              variant="destructive" 
              className="w-full" 
              onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      )}

      {/* Stats Cards - Horizontal Scroll on Mobile */}
      <div className="px-4 py-4 overflow-x-auto">
        <div className="flex gap-3 min-w-max">
          <Card className="bg-card border-border w-[140px] flex-shrink-0">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Briefcase className="h-8 w-8 text-primary opacity-60" />
                <div>
                  <p className="text-2xl font-bold">{activeJobs}</p>
                  <p className="text-xs text-muted-foreground">Active Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border w-[140px] flex-shrink-0">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-8 w-8 text-blue-400 opacity-60" />
                <div>
                  <p className="text-2xl font-bold">{pendingPOs}</p>
                  <p className="text-xs text-muted-foreground">Pending POs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border w-[140px] flex-shrink-0">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-8 w-8 text-green-400 opacity-60" />
                <div>
                  <p className="text-2xl font-bold">{upcomingSchedule}</p>
                  <p className="text-xs text-muted-foreground">Upcoming</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border w-[160px] flex-shrink-0">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <DollarSign className="h-8 w-8 text-yellow-400 opacity-60" />
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalPOValue)}</p>
                  <p className="text-xs text-muted-foreground">Total POs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <main className="px-4">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {[
            { id: 'jobs', label: 'Jobs', icon: Briefcase, count: jobs.length },
            { id: 'pos', label: 'Purchase Orders', icon: ShoppingCart, count: pos.length },
            { id: 'schedule', label: 'Schedule', icon: Calendar, count: schedule.length },
          ].map((tab) => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab.id)}
              className="flex-shrink-0"
            >
              <tab.icon className="h-4 w-4 mr-1" />
              {tab.label}
              {tab.count > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {tab.count}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div className="space-y-3">
            {jobs.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-8 text-center">
                  <Briefcase className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No jobs assigned</p>
                </CardContent>
              </Card>
            ) : (
              jobs.map((job) => {
                const StatusIcon = statusConfig[job.status]?.icon || Clock;
                return (
                  <Card 
                    key={job.id} 
                    className="bg-card border-border active:scale-[0.98] transition-transform"
                    onClick={() => setSelectedJob(selectedJob?.id === job.id ? null : job)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold truncate">{job.title}</h3>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{job.address || 'No address'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span className={`px-2 py-1 rounded-full text-xs flex items-center gap-1 ${statusConfig[job.status]?.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {statusConfig[job.status]?.label}
                          </span>
                          <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${selectedJob?.id === job.id ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                      
                      {/* Expanded Job Details */}
                      {selectedJob?.id === job.id && (
                        <div className="mt-4 pt-4 border-t border-border space-y-3">
                          {job.description && (
                            <p className="text-sm text-muted-foreground">{job.description}</p>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {job.client_phone && (
                              <a 
                                href={`tel:${job.client_phone}`}
                                className="flex items-center gap-1 px-3 py-2 bg-primary/10 rounded-lg text-sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Phone className="h-4 w-4" /> Call Client
                              </a>
                            )}
                            {job.address && (
                              <a 
                                href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MapPin className="h-4 w-4" /> Directions
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Purchase Orders Tab */}
        {activeTab === 'pos' && (
          <div className="space-y-3">
            {pos.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-8 text-center">
                  <ShoppingCart className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No purchase orders</p>
                </CardContent>
              </Card>
            ) : (
              pos.map((po) => (
                <Card key={po.id} className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold">{po.po_number}</h3>
                        <p className="text-sm text-muted-foreground">{po.job?.title}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${statusConfig[po.status]?.color}`}>
                        {statusConfig[po.status]?.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <div>
                        <p className="text-lg font-bold">{formatCurrency(po.total)}</p>
                        <p className="text-xs text-muted-foreground">{po.items?.length || 0} items</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleDownloadPO(po.id)}>
                        <Download className="h-4 w-4 mr-1" /> PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-3">
            {schedule.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-8 text-center">
                  <Calendar className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No scheduled work</p>
                </CardContent>
              </Card>
            ) : (
              schedule.map((item) => {
                const isUpcoming = new Date(item.start_datetime) > new Date();
                return (
                  <Card key={item.id} className={`bg-card border-border ${!isUpcoming ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center ${isUpcoming ? 'bg-primary/20 text-primary' : 'bg-secondary'}`}>
                          <span className="text-xs font-bold">{formatDate(item.start_datetime).split(' ')[1]}</span>
                          <span className="text-lg font-bold">{formatDate(item.start_datetime).split(' ')[0]}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{item.title}</h3>
                          <p className="text-sm text-muted-foreground">{item.job?.title}</p>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <Clock className="h-3 w-3" />
                            {formatDateTime(item.start_datetime)}
                          </div>
                        </div>
                        {isUpcoming && (
                          <Badge className="bg-green-500/20 text-green-400">Upcoming</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Bottom Navigation - Fixed */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border safe-area-bottom z-40">
        <div className="flex justify-around py-2">
          {[
            { id: 'jobs', label: 'Jobs', icon: Briefcase },
            { id: 'pos', label: 'Orders', icon: ShoppingCart },
            { id: 'schedule', label: 'Schedule', icon: Calendar },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-xs">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default TradePortalPage;
